/**
 * monthlyHrRecap.js — 09/02/2026 (Q2 Lot G user prod)
 *
 * Récapitulatif RH mensuel envoyé dans la Messagerie par l'Équipe RH.
 * Consolide les indicateurs clés du mois écoulé pour éviter au joueur
 * la noyade de notifications individuelles. Demandé explicitement
 * (« on garderait une vraie respiration RH manager »).
 *
 * Contenu :
 *   1. Effectif (chauffeurs hired, en formation, en préavis)
 *   2. Vivier candidats (nouveaux/sortis du pool, taille visible)
 *   3. Préavis de départ en cours (liste explicite + jours restants)
 *   4. Fatigue moyenne + satisfaction moyenne (avec emoji)
 *   5. Coûts salariaux du mois (info contextuelle)
 *
 * Le récap est SILENCIEUX si pas de chauffeurs (early game, avant
 * première embauche).
 */

/**
 * Calcule les KPI RH du mois écoulé. Retourne un objet structuré
 * exploitable par UI/tests, ou null si pas d'équipe.
 */
export function computeMonthlyHrKpis(state) {
  const drivers = state?.drivers || [];
  if (drivers.length === 0) return null;
  const today = state?.totalDays || 0;

  const inTraining = drivers.filter(d => d.status === 'training').length;
  const inHoliday = drivers.filter(d => d.status === 'holiday').length;
  const onMission = drivers.filter(d => d.status === 'driving' || d.status === 'on_mission').length;
  const pending = drivers
    .filter(d => d.pendingDeparture?.noticeUntil)
    .map(d => ({
      id: d.id,
      name: d.name,
      daysLeft: Math.max(0, (d.pendingDeparture.noticeUntil || 0) - today),
      expectedRaise: d.pendingDeparture.expectedRaise || 0,
    }));

  const fatigues = drivers.map(d => d.fatigue || 0);
  const sats = drivers.map(d => d.satisfaction ?? 70);
  const avg = (arr) => arr.reduce((s, v) => s + v, 0) / Math.max(1, arr.length);
  const avgFatigue = Math.round(avg(fatigues));
  const avgSat = Math.round(avg(sats));

  const salaryCost = drivers.reduce((s, d) => s + (d.employerCost || 0), 0);

  const poolSize = (state?.driverPool?.availableIds || []).length;
  const poolTaken = Object.keys(state?.driverPool?.takenBy || {}).length;

  return {
    headcount: drivers.length,
    inTraining,
    inHoliday,
    onMission,
    pending,
    avgFatigue,
    avgSat,
    salaryCost,
    poolSize,
    poolTaken,
  };
}

/**
 * Génère le payload de message pour `addMessage()`. Retourne null si
 * pas d'équipe à récapituler.
 */
export function buildMonthlyHrRecapMessage(state) {
  const kpis = computeMonthlyHrKpis(state);
  if (!kpis) return null;

  // Emojis d'humeur selon les KPI.
  const fatigueIcon = kpis.avgFatigue < 40 ? '🟢' : kpis.avgFatigue < 65 ? '🟡' : '🔴';
  const satIcon = kpis.avgSat >= 75 ? '😀' : kpis.avgSat >= 55 ? '🙂' : kpis.avgSat >= 35 ? '😐' : '😟';

  const lines = [];
  lines.push(`📊 **Effectif : ${kpis.headcount} chauffeur(s)**`);
  const status = [];
  if (kpis.onMission > 0) status.push(`${kpis.onMission} en mission`);
  if (kpis.inTraining > 0) status.push(`${kpis.inTraining} en formation`);
  if (kpis.inHoliday > 0) status.push(`${kpis.inHoliday} en congés`);
  if (status.length > 0) lines.push(`   ${status.join(' · ')}`);
  lines.push('');

  lines.push(`${fatigueIcon} **Fatigue moyenne : ${kpis.avgFatigue}/100**`);
  lines.push(`${satIcon} **Satisfaction moyenne : ${kpis.avgSat}/100**`);
  lines.push('');

  if (kpis.pending.length > 0) {
    lines.push(`⚠️ **${kpis.pending.length} chauffeur(s) en préavis de départ**`);
    kpis.pending.forEach(p => {
      const tag = p.expectedRaise > 0 ? ` (attente : +${p.expectedRaise} €/mois)` : '';
      lines.push(`   • **${p.name}** — départ J-${p.daysLeft}${tag}`);
    });
    lines.push(`   → Pour les retenir, alignez leur salaire via la **Messagerie**.`);
    lines.push('');
  }

  lines.push(`👥 **Vivier candidats : ${kpis.poolSize} profil(s)**${kpis.poolTaken > 0 ? ` (${kpis.poolTaken} pris par concurrents)` : ''}`);
  lines.push('');
  lines.push(`💸 **Coût salarial mensuel : ${kpis.salaryCost.toLocaleString('fr-FR')} €**`);

  return {
    kind: 'info',
    sender: 'Équipe RH',
    title: 'Récap RH mensuel',
    body: lines.join('\n'),
    ttlDays: 32,
  };
}
